/**
 * DTC Summary Dashboard
 * Single source of truth for all DTC platform analytics
 * 
 * DATA FLOW:
 * 1. Single API call to /api/dtc/kpis returns aggregated data + per-platform breakdown
 * 2. Platform cards use `by_platform` data from API (no separate fetches)
 * 3. All KPIs sourced from dtc_sales_summary table
 */

import React, { useState } from 'react'
import { DollarSign, ShoppingCart, TrendingUp, Package, Target, ArrowRight, RefreshCw, ExternalLink, BarChart3, Zap } from 'lucide-react'
import { Link } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import { useDtcKpis, useDtcPlatforms, useAggregateDtcSales, formatDtcCurrency, formatDtcNumber } from '../../lib/dtc-data-service'
import { apiClient } from '../../lib/api-client'
import { CalendarRange, getCalendarDays } from '../../components/DateRangeSelector'
import DtcHeader from '../../components/DtcHeader'
import HeroKpiCard from '../../components/HeroKpiCard'
import DtcRevenueTrendChart from '../../components/DtcRevenueTrendChart'
import DtcSummaryInsightsPanel from '../../components/DtcSummaryInsightsPanel'

// Platform colors (consistent across app)
const PLATFORM_COLORS: Record<string, { primary: string, light: string, icon: string }> = {
  shopify: { primary: '#95BF47', light: '#F0F7E8', icon: '🛍️' },
  amazon_seller_central: { primary: '#FF9900', light: '#FFF3E0', icon: '📦' },
  walmart: { primary: '#0071CE', light: '#E3F2FD', icon: '🏪' }
}

const getPlatformColor = (type: string) => PLATFORM_COLORS[type] || { primary: '#9333EA', light: '#F3E8FF', icon: '🛒' }

// Platform route mapping
const getPlatformRoute = (type: string) => {
  switch (type) {
    case 'walmart': return '/dtc/walmart'
    case 'amazon_seller_central': return '/dtc/amazon'
    default: return '/dtc/shopify'
  }
}

/**
 * Platform Card - Uses data from parent (no separate API call)
 */
interface PlatformData {
  platform_id: string
  platform_name: string
  platform_type: string
  revenue: number
  orders: number
  units: number
  avg_order_value: number
  revenue_growth_prev?: number | null
  orders_growth_prev?: number | null
}

function PlatformCard({ data, totalRevenue, platform }: { 
  data: PlatformData
  totalRevenue: number 
  platform: any
}) {
  const color = getPlatformColor(data.platform_type)
  const route = getPlatformRoute(data.platform_type)
  const percentOfTotal = totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0

  return (
    <Link href={route} className="block group">
        <div 
          className="bg-white rounded-xl border-2 p-5 hover:shadow-lg transition-all cursor-pointer h-full"
          style={{ borderColor: color.primary, backgroundColor: color.light }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{color.icon}</span>
              <div>
                <h3 className="font-bold text-gray-900">{data.platform_name}</h3>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {data.platform_type.replace('_', ' ')}
                </p>
              </div>
            </div>
            <ArrowRight 
              className="w-5 h-5 group-hover:translate-x-1 transition-transform" 
              style={{ color: color.primary }}
            />
          </div>

          {/* Metrics */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Revenue (period)</span>
              <div className="text-right">
                <span className="font-bold text-gray-900">{formatDtcCurrency(data.revenue)}</span>
                {data.revenue_growth_prev !== null && data.revenue_growth_prev !== undefined && (
                  <div className={`text-xs font-semibold ${data.revenue_growth_prev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {data.revenue_growth_prev >= 0 ? '↑' : '↓'} {Math.abs(data.revenue_growth_prev * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Orders</span>
              <div className="text-right">
                <span className="font-medium text-gray-700">{formatDtcNumber(data.orders)}</span>
                {data.orders_growth_prev !== null && data.orders_growth_prev !== undefined && (
                  <div className={`text-xs ${data.orders_growth_prev >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {data.orders_growth_prev >= 0 ? '↑' : '↓'} {Math.abs(data.orders_growth_prev * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">% of Total DTC</span>
              <span className="font-bold" style={{ color: color.primary }}>{percentOfTotal.toFixed(0)}%</span>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
            <span className={`text-xs px-2 py-1 rounded-full ${
              platform?.sync_status === 'success' ? 'bg-green-100 text-green-700' :
              platform?.sync_status === 'syncing' ? 'bg-yellow-100 text-yellow-700' :
              platform?.sync_status === 'error' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {platform?.sync_status === 'success' ? '✓ Synced' : 
               platform?.sync_status === 'syncing' ? '⏳ Syncing' :
               platform?.sync_status === 'error' ? '⚠ Error' : '○ Pending'}
            </span>
            <span className="text-xs font-medium" style={{ color: color.primary }}>
              Click for details →
            </span>
          </div>
        </div>
    </Link>
  )
}

/**
 * Marketing Summary Card
 */
function MarketingSummaryCard({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data: googleAdsData } = useQuery({
    queryKey: ['google-ads-summary', startDate, endDate],
    queryFn: () => apiClient.get(`/dtc/google-ads-metrics?start_date=${startDate || ''}&end_date=${endDate || ''}`),
    enabled: !!startDate && !!endDate
  })

  const { data: metaData } = useQuery({
    queryKey: ['meta-ads-summary', startDate, endDate],
    queryFn: () => apiClient.get(`/dtc/meta-ads-metrics?start_date=${startDate || ''}&end_date=${endDate || ''}`),
    enabled: !!startDate && !!endDate
  })

  const { data: klaviyoData } = useQuery({
    queryKey: ['klaviyo-summary', startDate, endDate],
    queryFn: () => apiClient.get(`/dtc/klaviyo-metrics?start_date=${startDate || ''}&end_date=${endDate || ''}`),
    enabled: !!startDate && !!endDate
  })

  const { data: tiktokData } = useQuery({
    queryKey: ['tiktok-summary', startDate, endDate],
    queryFn: () => apiClient.get(`/dtc/tiktok-ads-metrics?start_date=${startDate || ''}&end_date=${endDate || ''}`),
    enabled: !!startDate && !!endDate
  })

  const googleAdsStatus = googleAdsData?.data_status || 'unavailable'
  const metaStatus = metaData?.data_status || 'unavailable'
  const klaviyoStatus = klaviyoData?.data_status || 'unavailable'
  const tiktokStatus = tiktokData?.data_status || 'unavailable'

  const googleAdsSpend = googleAdsStatus === 'available' ? googleAdsData?.summary?.total_spend ?? null : null
  const metaSpend = metaStatus === 'available' ? metaData?.summary?.total_spend ?? null : null
  const klaviyoSpend = klaviyoStatus === 'available' ? klaviyoData?.summary?.total_spend ?? null : null
  const tiktokSpend = tiktokStatus === 'available' ? tiktokData?.summary?.total_spend ?? null : null

  const googleAdsRevenue = googleAdsStatus === 'available' ? googleAdsData?.summary?.conversion_value ?? null : null
  const metaRevenue = metaStatus === 'available' ? metaData?.summary?.conversion_value ?? null : null
  const klaviyoRevenue = klaviyoStatus === 'available' ? klaviyoData?.summary?.total_revenue ?? null : null
  const tiktokRevenue = tiktokStatus === 'available' ? tiktokData?.summary?.conversion_value ?? null : null

  const spendParts = [googleAdsSpend, metaSpend, klaviyoSpend, tiktokSpend].filter((v): v is number => typeof v === 'number')
  const revenueParts = [googleAdsRevenue, metaRevenue, klaviyoRevenue, tiktokRevenue].filter((v): v is number => typeof v === 'number')

  const totalAdSpend = spendParts.length ? spendParts.reduce((a, b) => a + b, 0) : null
  const totalAdRevenue = revenueParts.length ? revenueParts.reduce((a, b) => a + b, 0) : null

  const blendedROAS =
    totalAdSpend !== null && totalAdRevenue !== null && totalAdSpend > 0
      ? totalAdRevenue / totalAdSpend
      : null

  const isUnavailable = googleAdsStatus !== 'available' && metaStatus !== 'available' && klaviyoStatus !== 'available' && tiktokStatus !== 'available'

  return (
    <Link href="/dtc/marketing" className="block group">
        <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 rounded-xl border-2 border-purple-300 p-5 hover:shadow-lg hover:border-purple-400 transition-all cursor-pointer h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📊</span>
              <h3 className="font-bold text-purple-900">Marketing Intelligence</h3>
            </div>
            <ArrowRight className="w-5 h-5 text-purple-600 group-hover:translate-x-1 transition-transform" />
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Ad Spend</span>
              <span className="font-bold text-gray-900">
                {totalAdSpend === null ? '—' : formatDtcCurrency(totalAdSpend)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Ad Revenue</span>
              <span className="font-bold text-green-600">
                {totalAdRevenue === null ? '—' : formatDtcCurrency(totalAdRevenue)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Blended ROAS</span>
              <span className={`font-bold ${
                blendedROAS === null ? 'text-gray-600' : blendedROAS >= 2 ? 'text-green-600' : blendedROAS >= 1 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {blendedROAS === null ? '—' : `${blendedROAS.toFixed(1)}x`}
              </span>
            </div>
            
            <div className="pt-2 border-t border-purple-200 flex flex-wrap gap-1">
              {googleAdsStatus === 'available' && <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">🔵 Google</span>}
              {metaStatus === 'available' && <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">📘 Meta</span>}
              {klaviyoStatus === 'available' && <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">📧 Klaviyo</span>}
              {tiktokStatus === 'available' && <span className="text-xs px-2 py-1 bg-pink-100 text-pink-700 rounded-full">🎵 TikTok</span>}
            </div>

            {isUnavailable && (
              <div className="text-xs text-purple-700 pt-1">
                Not integrated (or no marketing data for this date range)
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-purple-200">
            <span className="text-xs text-purple-600 font-semibold">View Full Marketing Dashboard →</span>
          </div>
        </div>
    </Link>
  )
}

/**
 * Main Summary Page
 */
export default function DtcSummary() {
  // Date range state
  const [calendarRange, setCalendarRange] = useState<CalendarRange>('last7')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [activeCustomDates, setActiveCustomDates] = useState<{start: string, end: string} | null>(null)
  
  // Aggregation state
  const [aggregationMessage, setAggregationMessage] = useState<string | null>(null)
  const aggregateSales = useAggregateDtcSales()
  
  // Fetch platforms (for sync status)
  const { data: platforms, isLoading: platformsLoading } = useDtcPlatforms()
  
  // Calculate date range
  let apiOptions: any = {}
  if (calendarRange === 'custom' && activeCustomDates?.start && activeCustomDates?.end) {
    apiOptions.start_date = activeCustomDates.start
    apiOptions.end_date = activeCustomDates.end
  } else if (calendarRange !== 'custom') {
    const end = new Date()
    const days = getCalendarDays(calendarRange)
    const start = new Date(end)
    start.setDate(start.getDate() - days + 1)
    apiOptions.start_date = start.toISOString().split('T')[0]
    apiOptions.end_date = end.toISOString().split('T')[0]
  }
  
  // SINGLE API CALL - returns summary + by_platform + trend
  const { data: kpiData, isLoading: kpisLoading, refetch } = useDtcKpis(null, undefined, apiOptions)
  
  const summary = kpiData?.summary
  const byPlatform = kpiData?.by_platform || []
  
  // Helper to get platform sync status
  const getPlatformById = (id: string) => platforms?.find(p => p.id === id)
  
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setActiveCustomDates({ start: customStartDate, end: customEndDate })
    }
  }
  
  const handleRefreshData = async () => {
    setAggregationMessage('Syncing data from all platforms...')
    try {
      const result = await aggregateSales.mutateAsync({ days: 90, force: false })
      if (result.success) {
        setAggregationMessage(`✅ Data refreshed successfully!`)
        setTimeout(() => { setAggregationMessage(null); refetch() }, 2000)
      }
    } catch (error) {
      setAggregationMessage('❌ Failed to refresh: ' + (error instanceof Error ? error.message : 'Unknown error'))
      setTimeout(() => setAggregationMessage(null), 5000)
    }
  }

  // Format comparison text
  const formatGrowth = (growth: number | null | undefined): string | undefined => {
    if (growth === null || growth === undefined) return undefined
    return `${growth >= 0 ? '+' : ''}${(growth * 100).toFixed(1)}% vs prev`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DtcHeader
        title="DTC Summary"
        subtitle="All Direct-to-Consumer Platforms • All dates in UTC (matches Shopify reports)"
        onRefresh={refetch}
        isLoading={kpisLoading || aggregateSales.isPending}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6 space-y-6">
        
        {/* Date Range Selector */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              📅 Date Range (UTC)
            </span>
            {apiOptions.start_date && apiOptions.end_date && (
              <span className="text-xs text-gray-600">
                {apiOptions.start_date} to {apiOptions.end_date} UTC
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {(['last7', 'last30', 'last90'] as CalendarRange[]).map(range => (
              <button 
                key={range}
                onClick={() => { setCalendarRange(range); setActiveCustomDates(null) }} 
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  calendarRange === range 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                }`}
              >
                {range === 'last7' ? 'Last 7 Days' : range === 'last30' ? 'Last 30 Days' : 'Last 90 Days'}
              </button>
            ))}
            <button 
              onClick={() => setCalendarRange('custom')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'custom' 
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
              }`}
            >
              📆 Custom Range
            </button>
          </div>

          {calendarRange === 'custom' && (
            <div className="mt-4 p-4 bg-white rounded-lg border-2 border-purple-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStartDate || !customEndDate}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                View Report
              </button>
            </div>
          )}
        </div>

        {/* Combined Performance KPIs */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Combined Performance (All Platforms)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <HeroKpiCard
              label="Total DTC Revenue"
              value={kpisLoading ? '...' : formatDtcCurrency(summary?.total_revenue || 0)}
              icon={DollarSign}
              yoyDeltaText={formatGrowth(summary?.revenue_growth_prev)}
              yoyDeltaTone={summary?.revenue_growth_prev !== undefined && summary?.revenue_growth_prev >= 0 ? 'success' : 'danger'}
            />
            <HeroKpiCard
              label="Total Orders"
              value={kpisLoading ? '...' : formatDtcNumber(summary?.total_orders || 0)}
              icon={ShoppingCart}
              yoyDeltaText={formatGrowth(summary?.orders_growth_prev)}
              yoyDeltaTone={summary?.orders_growth_prev !== undefined && summary?.orders_growth_prev >= 0 ? 'success' : 'danger'}
            />
            <HeroKpiCard
              label="Avg Order Value"
              value={kpisLoading ? '...' : formatDtcCurrency(summary?.avg_order_value || 0)}
              icon={Target}
              yoyDeltaText={formatGrowth(summary?.aov_growth_prev)}
              yoyDeltaTone={summary?.aov_growth_prev !== undefined && summary?.aov_growth_prev >= 0 ? 'success' : 'danger'}
            />
            <HeroKpiCard
              label="Total Units"
              value={kpisLoading ? '...' : formatDtcNumber(summary?.total_units || 0)}
              icon={Package}
              yoyDeltaText={formatGrowth(summary?.units_growth_prev)}
              yoyDeltaTone={summary?.units_growth_prev !== undefined && summary?.units_growth_prev >= 0 ? 'success' : 'danger'}
            />
          </div>
        </div>

        {/* Revenue Trend Chart - Moved up per user request */}
        {kpiData?.trend && kpiData.trend.length > 0 && (
          <DtcRevenueTrendChart 
            data={kpiData.trend} 
            platforms={platforms}
            days={calendarRange === 'last7' ? 7 : calendarRange === 'last30' ? 30 : 90}
          />
        )}

        {/* Platform Cards Grid - Uses by_platform from single API call */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">DTC Platforms</h2>
              <p className="text-sm text-gray-600">Click a platform to view detailed analytics</p>
            </div>
            <button
              onClick={handleRefreshData}
              disabled={aggregateSales.isPending}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-all flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${aggregateSales.isPending ? 'animate-spin' : ''}`} />
              {aggregateSales.isPending ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
          
          {aggregationMessage && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
              aggregationMessage.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' :
              aggregationMessage.includes('❌') ? 'bg-red-50 text-red-700 border border-red-200' :
              'bg-blue-50 text-blue-700 border border-blue-200'
            }`}>
              {aggregationMessage}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Platform cards from by_platform data */}
            {byPlatform.map((platformData: PlatformData) => (
              <PlatformCard
                key={platformData.platform_id}
                data={platformData}
                totalRevenue={summary?.total_revenue || 0}
                platform={getPlatformById(platformData.platform_id)}
              />
            ))}

            {/* Marketing Intelligence Card */}
            <MarketingSummaryCard startDate={apiOptions.start_date} endDate={apiOptions.end_date} />

            {/* Add Platform Card */}
            <Link href="/dtc/settings" className="block">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300 p-5 flex flex-col items-center justify-center text-center h-full min-h-[200px] hover:border-gray-400 transition-colors">
                  <Package className="w-10 h-10 text-gray-400 mb-3" />
                  <h3 className="font-semibold text-gray-700 mb-1">Add Platform</h3>
                  <p className="text-xs text-gray-500 mb-3">Connect additional DTC channels</p>
                  <span className="px-3 py-1.5 bg-gray-600 text-white text-xs font-medium rounded-lg">
                    Platform Settings
                  </span>
                </div>
            </Link>
          </div>
        </div>

        {/* AI Insights Panel */}
        {apiOptions.start_date && apiOptions.end_date && platforms && platforms.length > 0 && (
          <DtcSummaryInsightsPanel
            platforms={platforms}
            startDate={apiOptions.start_date}
            endDate={apiOptions.end_date}
          />
        )}

        {/* Data Source Footer */}
        <div className="text-center text-xs text-gray-500 py-4 border-t border-gray-200">
          <span className="font-medium">Data Source:</span> dtc_sales_summary • 
          <span className="font-medium ml-2">Period:</span> {apiOptions.start_date} to {apiOptions.end_date}
        </div>
      </div>
    </div>
  )
}
